import { Redirect, Route, useSearch } from "wouter";

import { Guarded } from "@/app/guards";
import { AdminPlatform, AlertsPage, PlatformActivity, PlatformPersonPage } from "@/app/lazy-pages";
import { PLATFORM_PERSON_ROUTE } from "@/lib/platform-person-route";
import { AppShellLayout } from "@/components/layout/app-shell";

import type { CurrentUser } from "@/app/app-routes";

/**
 * `/admin/platform/audit` is a retired tab id whose successor is a PAGE, not a
 * tab (LEGACY_PLATFORM_TAB_REDIRECTS maps `audit -> "activity"`), so
 * `validTab()` cannot resolve it — it needs a real navigation. `replace` keeps
 * the dead URL out of history, and the query string is carried through the
 * redirect rather than dropped, so whatever the Activity surface reads from it
 * still arrives. `?focus=<id>` IS consumed at the destination as of module 04
 * Task 3 (Ruling W): platform-activity-console.tsx reads it via `useFocusParam`
 * (use-focus-highlight.ts) and highlights the matching row, so the hop lands
 * the reader on the row they came from; this route guarantees the query
 * survives it.
 */
function LegacyAuditRedirect(): JSX.Element {
  const search = useSearch();
  const query = search.replace(/^\?/, "");
  return <Redirect replace to={`/admin/platform/activity${query ? `?${query}` : ""}`} />;
}

interface AdminRouteContext { user: CurrentUser | null; loading: boolean; validatePlatformTab: (tab: string | undefined) => void; }

/** Build ordered platform-admin routes as direct children for Wouter's outer Switch. */
export function buildAdminRoutes(context: AdminRouteContext): JSX.Element {
  const adminShellPage = (page: JSX.Element): JSX.Element => (
    <Guarded user={context.user} loading={context.loading} minAal="AAL3">
      <AppShellLayout displayName={context.user?.displayName} aal={context.user?.aal}>{page}</AppShellLayout>
    </Guarded>
  );
  return <>
    {/* One person, as seen from ONE organisation (module 06 Task 3). Registered
        from the shared constant, never a re-typed literal, so this and
        `platformPersonHref` cannot drift. It precedes the org drill-in
        deliberately: the tail here is TWO segments (`people/<ref>`), which
        `/:tab?` cannot match, but order is the guarantee, not the arithmetic. */}
    <Route path={PLATFORM_PERSON_ROUTE}>{adminShellPage(<PlatformPersonPage />)}</Route>
    <Route path="/admin/platform/org/:orgId/:tab?">{adminShellPage(<AdminPlatform />)}</Route>
    <Route path="/admin/platform/alerts/:clusterId?">{adminShellPage(<AlertsPage context={{ type: "platform" }} />)}</Route>
    {/* Activity is a destination, not a tab. Both of these MUST precede
        "/admin/platform/:tab?" — wouter's Switch takes the first match, and
        the fallback would otherwise swallow them and render the console with
        an unknown tab (silently stranding the visitor on Home). */}
    <Route path="/admin/platform/activity">{adminShellPage(<PlatformActivity />)}</Route>
    <Route path="/admin/platform/audit"><LegacyAuditRedirect /></Route>
    <Route path="/admin/platform/:tab?">
      {(params) => { context.validatePlatformTab(params.tab); return adminShellPage(<AdminPlatform />); }}
    </Route>
  </>;
}
